import { useSelector } from 'react-redux';

import { ReactComponent as BankAccount } from 'images/step-icons/bank-account-solid.svg';
import { ReactComponent as BriefCase } from 'images/step-icons/brief-case-solid.svg';
import { ReactComponent as License } from 'images/step-icons/license-solid.svg';
import EmploymentDetailsStep from 'components/Verification/Steps/EmploymentDetails/EmploymentDetails';
import VerifyIdentityStep from 'components/Verification/Steps/VerifyIdentity';
import CheckingAccountStep from 'components/Verification/Steps/CheckingAccount/Plaid/Plaid';
import { getApplicationData } from 'selectors/getApplicationData';
import { completeVerificationStep, setCurrentVerificationStep } from 'handlers/applicationData';
import { FlowComponentType } from 'routes/types';
import { StepsNavigation } from 'components/Steps/StepsNavigation/StepsNavigation';
import { useSteps, StepData } from 'components/Steps/stepUtils';
import FirstTimeFlow from 'components/Steps/StepsFlow/FirstTimeFlow';
import ReturningFlow from 'components/Steps/StepsFlow/ReturningFlow';
import { RoutePath } from 'enums/Routes';

export enum VerificationStep {
  EmploymentDetails = 'employmentDetails',
  UploadPaystubs = 'paystubs',
  CheckingAccount = 'bankAccount',
  VerifyIdentity = 'kyc',
}

export enum IncomeVerificationType {
  PaystubsUpload = 'paystubsUpload',
}

export type VerificationStepProgress = {
  employmentDetails: boolean;
  paystubs: boolean;
  bankAccount: boolean;
  kyc: boolean;
};

export const defaultVerificationProgress: Readonly<VerificationStepProgress> = Object.freeze({
  [VerificationStep.EmploymentDetails]: false,
  [VerificationStep.UploadPaystubs]: false,
  [VerificationStep.CheckingAccount]: false,
  [VerificationStep.VerifyIdentity]: false,
});

export const getVerificationSteps: () => StepData<VerificationStep>[] = () => [
  {
    id: VerificationStep.EmploymentDetails,
    name: 'Employment Details',
    component: EmploymentDetailsStep,
    icon: BriefCase,
  },
  {
    id: VerificationStep.CheckingAccount,
    name: 'Connect Checking Account',
    component: CheckingAccountStep,
    icon: BankAccount,
  },
  {
    id: VerificationStep.VerifyIdentity,
    name: 'Verify Identity',
    component: VerifyIdentityStep,
    icon: License,
  },
];

export const useVerificationSteps = () => {
  const { application, currentVerificationStep } = useSelector(getApplicationData);
  const verificationStepsStatus = application?.verificationStepProgress ?? defaultVerificationProgress;

  const verificationSteps = getVerificationSteps();

  return useSteps(
    currentVerificationStep ?? verificationSteps[0].id,
    setCurrentVerificationStep,
    completeVerificationStep,
    verificationSteps,
    verificationStepsStatus,
    RoutePath.VerifyingApplication,
  );
};

export const VerificationNavigation = (args: FlowComponentType) =>
  StepsNavigation<VerificationStep, VerificationStepProgress>(args, useVerificationSteps);
export const VerificationFirstTimeFlow = () =>
  FirstTimeFlow<VerificationStep, VerificationStepProgress>(useVerificationSteps);
export const VerificationReturningFlow = () =>
  ReturningFlow<VerificationStep, VerificationStepProgress>(useVerificationSteps, RoutePath.VerificationSteps);
